import * as Sentry from "@sentry/react";

let _nativeAppVersion: string | null = null;

/** Stores the native app version for inclusion in health breadcrumbs and exceptions. */
export function setNativeAppVersion(version: string) {
  _nativeAppVersion = version;
}

/** Returns the native app version, or null if the native shell never completed initialization. */
export function getNativeAppVersion(): string | null {
  return _nativeAppVersion;
}

function getVersionContext(): {
  webAppVersion: string | undefined;
  nativeAppVersion: string | null;
} {
  return {
    webAppVersion: import.meta.env.VITE_APP_VERSION,
    nativeAppVersion: _nativeAppVersion,
  };
}

export function initialiseApplicationMonitoring() {
  if (import.meta.env.VITE_APP_SENTRY_DSN?.length) {
    Sentry.init({
      dsn: import.meta.env.VITE_APP_SENTRY_DSN,
      environment: import.meta.env.VITE_APP_SENTRY_ENVIRONMENT,
      maxValueLength: 5000,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: Number(
        import.meta.env.VITE_APP_SENTRY_TRACES_SAMPLE_RATE
      ),
      ignoreErrors: [
        // Binah SDK: canvas getImageData fails after WASM worker crash on iOS Safari
        /InvalidStateError.*object is in an invalid state/i,
      ],
    });
  }
}

export function captureException(err: any) {
  Sentry.captureException(err);
}

/**
 * Captures a HealthKit/Health Connect sync error with structured context.
 * All errors are tagged with `feature:health-data-sync` for Sentry filtering.
 *
 * @param err - The error to capture
 * @param context - Structured context for debugging (payload size, retry count, etc.)
 */
export function captureHealthException(
  err: unknown,
  context: Record<string, unknown>
) {
  Sentry.withScope((scope) => {
    scope.setTag("feature", "health-data-sync");
    scope.setContext("healthSync", { ...getVersionContext(), ...context });
    Sentry.captureException(err);
  });
}

export function addHealthSyncBreadcrumb(
  message: string,
  context?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    category: "health-sync",
    message,
    data: { ...getVersionContext(), ...context },
    level: "info",
  });
}

export function setSentryUser(memberId: string) {
  Sentry.setUser({ id: memberId });
}

export function clearSentryUser() {
  Sentry.setUser(null);
}
